import { BOARD_TOOL_IDS, type BoardToolId } from "./types";

export const DEFAULT_TOOL_KEY_BINDINGS: Record<BoardToolId, string> = {
  select: "KeyV",
  hand: "KeyH",
  pen: "KeyP",
  line: "KeyL",
  arrow: "KeyA",
  shape: "KeyR",
  frame: "KeyF",
  text: "KeyT",
  sticky: "KeyN",
  eraser: "KeyE",
};

export const TOOL_KEYBINDING_LABELS: Record<BoardToolId, string> = {
  select: "Курсор — выделение и перенос",
  hand: "Рука — сдвиг холста",
  pen: "Ручка",
  line: "Линия",
  arrow: "Стрелка",
  shape: "Фигуры",
  frame: "Рамка",
  text: "Текст",
  sticky: "Стикер",
  eraser: "Ластик",
};

const STORAGE_KEY = "wb-board-keybindings-v1";

const EXTRA_BINDING_CODES = new Set([
  "BracketLeft",
  "BracketRight",
  "Semicolon",
  "Quote",
  "Comma",
  "Period",
  "Slash",
  "Backquote",
  "Minus",
  "Equal",
]);

const CODE_LABELS: Record<string, string> = {
  BracketLeft: "[",
  BracketRight: "]",
  Semicolon: ";",
  Quote: "'",
  Comma: ",",
  Period: ".",
  Slash: "/",
  Backquote: "`",
  Minus: "-",
  Equal: "=",
};

function isBoardToolId(value: string): value is BoardToolId {
  return (BOARD_TOOL_IDS as readonly string[]).includes(value);
}

function loadBindings(): Record<BoardToolId, string> {
  const next = { ...DEFAULT_TOOL_KEY_BINDINGS };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return next;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    for (const id of BOARD_TOOL_IDS) {
      const code = parsed[id];
      if (typeof code === "string" && isAllowedBindingCode(code)) {
        next[id] = code;
      }
    }
  } catch {
    /* ignore */
  }
  return next;
}

function persistBindings(next: Record<BoardToolId, string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

let bindings: Record<BoardToolId, string> = loadBindings();

export function boardKeybindingsEventName(): string {
  return "wb-board-keybindings";
}

function notifyBindingsChanged(): void {
  window.dispatchEvent(new Event(boardKeybindingsEventName()));
}

export function subscribeBoardKeybindings(onChange: () => void): () => void {
  const handler = () => onChange();
  window.addEventListener(boardKeybindingsEventName(), handler);
  return () => window.removeEventListener(boardKeybindingsEventName(), handler);
}

export function getToolKeyBindings(): Record<BoardToolId, string> {
  // useSyncExternalStore: стабильная ссылка, пока bindings не менялись.
  return bindings;
}

export function getToolKeyBinding(toolId: BoardToolId): string {
  return bindings[toolId] ?? DEFAULT_TOOL_KEY_BINDINGS[toolId];
}

export function isAllowedBindingCode(code: string): boolean {
  if (!code || code === "Escape" || code === "Tab") return false;
  if (code.startsWith("Key")) return true;
  if (code.startsWith("Digit")) return true;
  if (/^F\d+$/.test(code)) return true;
  return EXTRA_BINDING_CODES.has(code);
}

export function formatKeyCode(code: string): string {
  if (code.startsWith("Key") && code.length === 4) return code.slice(3);
  if (code.startsWith("Digit") && code.length === 6) return code.slice(5);
  if (/^F\d+$/.test(code)) return code;
  return CODE_LABELS[code] ?? code;
}

export function getToolShortcutLabel(toolId: BoardToolId): string {
  return formatKeyCode(getToolKeyBinding(toolId));
}

export function findToolByKeyCode(code: string, map = bindings): BoardToolId | null {
  for (const id of BOARD_TOOL_IDS) {
    if (map[id] === code) return id;
  }
  return null;
}

export function toolIdForKeyCode(code: string): BoardToolId | null {
  return findToolByKeyCode(code);
}

/** Назначить клавишу инструменту; при конфликте — меняет местами. */
export function setToolKeyBinding(toolId: BoardToolId, code: string): boolean {
  if (!isAllowedBindingCode(code)) return false;
  const next = { ...bindings };
  const prev = next[toolId];
  const other = findToolByKeyCode(code, next);
  if (other && other !== toolId) {
    next[other] = prev;
  }
  next[toolId] = code;
  bindings = next;
  persistBindings(bindings);
  notifyBindingsChanged();
  return true;
}

export function resetToolKeyBindings(): void {
  bindings = { ...DEFAULT_TOOL_KEY_BINDINGS };
  persistBindings(bindings);
  notifyBindingsChanged();
}
